// What? A React component for the search interface.
// Why? To provide a search interface for finding and viewing company information.
// How? The component's logic flow:
// 1. Initialize: Set up state for search results and filters
// 2. API Interaction: Use useAuthFetch for authenticated calls to backend
// 3. User Input: Handle search queries and filter changes
// 4. UI Management: Render search results in a table format
// 5. Navigation: Use useNavigate for viewing company profiles
// 6. Pagination: Handle result pagination

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { CatalystEventSearchResults } from "./CatalystEventSearchResults";
import type { FilterInterface } from "./SearchFilters";
import { buildFilterDoc, type FilterValues } from "./filterState";
import { ActiveFilters } from "./ActiveFilters";
import { FilterRailLayout, RAIL_PAGE_SX } from "./FilterRail";
import { CATALYST_EVENT_PRIMARY_FACETS } from "./filterFacets";
import {
  buildCompanyHref,
  COMPANY_PROFILE_BACK_SOURCE,
} from "../utils/profileBackNavigation";

import {
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Card,
  CardContent,
} from "@mui/joy";
import DownloadIcon from "@mui/icons-material/Download";
import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../components/Tutorial";

import {
  useBKGCatalystEventSearch,
  useBKGCatalystEventExport,
  useBKGIndexCatalystEventFilter,
} from "../hooks";
import { convertToCSV, downloadCSV } from "../utils/exportUtils";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";

// Helper function to compare if two objects are equal or not
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }

  return true;
};

const Event: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const itemsPerPage = 50;
  const [filterParams, setFilterParams] = useState<FilterInterface | undefined>(
    undefined,
  );
  const [queryParams, setQueryParams] = useState<any | undefined>({
    skip: 0,
    limit: itemsPerPage,
    catalyst_event_filter: {},
  });

  const [results, setResults] = useState<any[]>([]);
  const [nextPage, setNextPage] = useState<number | undefined>();
  const [prevPage, setPrevPage] = useState<number | undefined>();

  const [totalResults, setTotalResults] = useState<number>(0);

  const BKGCatalystEventSearchAPI = useBKGCatalystEventSearch();
  const BKGCatalystEventExportAPI = useBKGCatalystEventExport();
  const BKGIndexCatalystEventFilter = useBKGIndexCatalystEventFilter();

  const [filterValues, setFilterValues] = useState<FilterValues>({});

  // One-shot hint introducing catalyst-event filtering.
  const filtersTutorialRef = useTutorialTarget("bkg-events-filters");
  useTutorialHint(
    {
      id: "bkg-events-intro",
      targetId: "bkg-events-filters",
      title: "Track catalyst events",
      body: "Search and filter catalyst events — readouts, approvals, conferences — the same way you filter companies.",
      placement: "bottom",
    },
    !loading && filterParams !== undefined,
  );

  // Set loading until workspace and filterParams has been set
  useEffect(() => {
    if (workspace && filterParams !== undefined) {
      setLoading(false);
    }
  }, [workspace, filterParams]);

  const fetchResults = useCallback(
    async (params: any) => {
      try {
        setLoading(true);
        setError(null);

        const response = await BKGCatalystEventSearchAPI(
          workspace!.id,
          params["queryParams"],
        );
        setResults(response.results);
        setTotalResults(response.total);
        setPrevPage(response?.prev_page);
        setNextPage(response?.next_page);
      } catch (error) {
        console.error("Error fetching event search results:", error);
        setError("Failed to load event search results. Please try again.");
        setResults([]);
        setTotalResults(0);
      } finally {
        setLoading(false);
      }
    },
    [workspace, BKGCatalystEventSearchAPI],
  );

  // Get search filters
  useEffect(() => {
    const fetch = async () => {
      try {
        const companyFilter = await BKGIndexCatalystEventFilter(workspace!.id);
        setFilterParams(companyFilter);
      } catch (error) {
        console.error("Error fetching catalyst event filter:", error);
        setError("Failed to load event filters. Please try again.");
        setLoading(false);
      }
    };

    if (workspace) {
      fetch();
    }
  }, [workspace, BKGIndexCatalystEventFilter]);

  // Get search data
  useEffect(() => {
    if (workspace && filterParams !== undefined) fetchResults({ queryParams });
  }, [workspace, filterParams, queryParams, fetchResults]);

  const onFilterChange = useCallback(
    (nextValues: FilterValues) => {
      setFilterValues(nextValues);
      const doc =
        (filterParams && buildFilterDoc(filterParams, nextValues)) || {};

      // Use functional update to avoid stale closure over queryParams
      setQueryParams((prev: typeof queryParams) => {
        // Only update if the queryParams are actually different
        if (!deepEqual(doc, prev?.catalyst_event_filter)) {
          return { ...prev, skip: 0, catalyst_event_filter: doc };
        }
        return prev;
      });
    },
    [filterParams],
  );

  const onChangePage = async (newPage: number) => {
    await setQueryParams({ ...queryParams, skip: newPage });
  };

  const handleExport = async () => {
    if (!workspace?.id) return;

    try {
      const filterDoc = queryParams.catalyst_event_filter || {};
      const response = await BKGCatalystEventExportAPI(workspace.id, {
        catalyst_event_filter:
          filterDoc && Object.keys(filterDoc).length ? filterDoc : undefined,
      });

      const rows = (response.results || []).map((catalyst_event: any) => {
        const company = catalyst_event?.value?.company_name;
        return {
          "Event Title": catalyst_event?.value?.title?.value || "",
          "Event Summary": stripSourceRefs(
            catalyst_event?.value?.summary?.value || "",
          ),
          "Event Type": catalyst_event?.value?.type?.value || "",
          "Event Date": catalyst_event?.value?.date?.value || "",
          "Report Date": catalyst_event?.source_date || "",
          "Company Name": company?.value?.name?.value || "",
        };
      });

      const csv = convertToCSV(rows);
      if (!csv) return;

      downloadCSV(csv, "catalyst_events_list.csv");
    } catch (err) {
      console.error("Failed to export catalyst events:", err);
      setError("Export failed. Please try again.");
    }
  };

  const getCompanyHref = (companyId: string): string => {
    if (!companyId || !workspace?.id) return "#";
    return buildCompanyHref(workspace.id, companyId, {
      from: COMPANY_PROFILE_BACK_SOURCE.catalystEvents,
    });
  };

  const handleCompanyClick = (companyId: string) => {
    if (!workspace?.id) return;
    navigate(
      buildCompanyHref(workspace.id, companyId, {
        from: COMPANY_PROFILE_BACK_SOURCE.catalystEvents,
      }),
      { state: { documentId: companyId } },
    );
  };

  // Results wait for the schema here, so an error before it arrives is its own.
  const schemaLoading = filterParams === undefined && !error;

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page sx={RAIL_PAGE_SX}>
      <PageHeader>
        <Typography level="h1">Catalyst Events</Typography>
        <Button
          startDecorator={<DownloadIcon />}
          variant="outlined"
          color="neutral"
          onClick={handleExport}
        >
          Export
        </Button>
      </PageHeader>

      <Stack spacing={3}>
        {/* Error Display */}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        <FilterRailLayout
          schema={filterParams}
          schemaLoading={schemaLoading}
          value={filterValues}
          onChange={onFilterChange}
          primaryFacets={CATALYST_EVENT_PRIMARY_FACETS}
          resultCount={totalResults}
          resultsLoading={loading}
          tutorialRef={filtersTutorialRef}
        >
          {/* Search Results */}
          <Card>
            <CardContent>
              {loading ? (
                <Loading />
              ) : (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Typography level="h3">Event Results</Typography>
                    <Typography level="body-sm" color="neutral">
                      {totalResults} result{totalResults !== 1 ? "s" : ""} found
                    </Typography>
                  </Stack>
                  <ActiveFilters
                    schema={filterParams}
                    values={filterValues}
                    onChange={onFilterChange}
                    sx={{ mb: 3 }}
                  />

                  <CatalystEventSearchResults
                    loading={loading}
                    results={results}
                    onCompanyClick={handleCompanyClick}
                    hrefForCompany={getCompanyHref}
                  />

                  {/* Pagination */}
                  {totalResults > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                      }}
                    >
                      <Typography level="body-sm" color="neutral">
                        Showing{" "}
                        {Math.min(queryParams["skip"] + 1, totalResults)}-
                        {Math.min(
                          queryParams["skip"] + itemsPerPage,
                          totalResults,
                        )}{" "}
                        of {totalResults} results
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {prevPage !== undefined && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={() => onChangePage(prevPage!)}
                            disabled={prevPage === undefined}
                          >
                            Previous
                          </Button>
                        )}
                        {nextPage !== undefined && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={() => onChangePage(nextPage!)}
                            disabled={nextPage === undefined}
                          >
                            Next
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </FilterRailLayout>
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default Event;
